import {ref, computed} from "vue"
import {defineStore, acceptHMRUpdate} from "pinia"
import {firestore, functions} from "@/firebase/index.js"
import {httpsCallable} from "firebase/functions"
import {
  collection,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore"
import {useFormSaveStateStore} from "@/stores/formSaveState.js"

export const usePlatformShopsStore = defineStore("platformShops", () => {
  // stores
  const formSaveStateStore = useFormSaveStateStore()

  // state
  const shops = ref([])
  const status = ref("init")
  const unsubscribe = ref(null)

  // getters
  const platformShopsLoaded = computed(() => status.value == "loaded")

  // actions
  async function bind() {
    const collectionRef = collection(firestore, `shops`)
    return await this.attach("shops", collectionRef)
  }

  async function unbind() {
    return await this.detach()
  }

  async function add() {
    const manageFn = httpsCallable(functions, "calls-admin-platform-manage")
    const response = await manageFn({action: "create-shop"})
    return {shopId: response.data.shopId}
  }

  async function update(args) {
    formSaveStateStore.markSaving(args.fieldId)

    const shopId = args.data.id
    const siteId = args.data.siteId
    const docRef = doc(firestore, `shops/${shopId}`)
    const data = {
      billing: args.data.billing,
      config: args.data.config,
      siteId: siteId || "",
      scope: args.data.scope,
      note: args.data.note || "",
      "meta.updated": serverTimestamp(),
    }
    const updateShop = updateDoc(docRef, data)
    let updates = [updateShop]

    // update shopId in site
    if (siteId) {
      let data = {
        shopId,
      }
      const siteRef = doc(firestore, `sites/${siteId}`)
      const updateSite = updateDoc(siteRef, data)
      updates.push(updateSite)
    }

    await Promise.all(updates)

    formSaveStateStore.markSaved(args)
    return
  }

  async function remove(args) {
    const shopId = args.shopId
    const docRef = doc(firestore, `shops/${shopId}`)
    return await deleteDoc(docRef)
  }

  return {
    // state
    shops,
    status,
    unsubscribe,

    // getters
    platformShopsLoaded,

    // actions
    bind,
    unbind,
    add,
    update,
    remove,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePlatformShopsStore, import.meta.hot))
}
